/**
 * Shared `routeDidChange` wiring for every app Router (was copy-pasted into
 * all 14 app/router.js constructors with small drifts): GA page_view, optional
 * GTM dataLayer push, scroll-to-top and dropdown cleanup on route change.
 *
 * Public-shop apps (booking / e-commerce) carry live bearer tokens in deep
 * links (`?access_token=…`) that must never reach Google Analytics / GTM —
 * they enable `stripToken`.
 */

function stripAccessToken(url) {
  if (!url || !url.includes('access_token')) {
    return url;
  }
  return url.replace(/([?&])access_token=[^&]*&?/g, '$1').replace(/[?&]$/, '');
}

/**
 * Call from the Router constructor (after `super`):
 *
 *   setupRouterTracking(this, { gaId: 'G-…' });
 *
 * @param {EmberRouter} router - the Router instance (`this`); needs the
 *   injected `router` service on it (optional-chained for unit-test containers
 *   that have no service — see commit 9212b3c).
 * @param {object} options
 * @param {string} options.gaId - GA4 measurement id for gtag page_view.
 * @param {string[]} [options.skipPrefixes] - URL prefixes to not track at all.
 * @param {boolean} [options.stripToken] - strip `access_token` from reported URLs.
 * @param {boolean} [options.pushDataLayer] - also push page_view to window.dataLayer (GTM).
 * @param {string|null} [options.scrollSelector] - what to scroll to top on route
 *   change; null disables (sciclub / cli never scrolled).
 * @param {number} [options.scrollDuration]
 * @param {boolean} [options.dropdownCleanup] - close `.data-dropdown` popups on
 *   route change (internal apps; the public shops never did this).
 */
function setupRouterTracking(router, {
  gaId,
  skipPrefixes = ['/token_login'],
  stripToken = false,
  pushDataLayer = false,
  scrollSelector = '.scroll-top-on-transition',
  scrollDuration = 300,
  dropdownCleanup = true
} = {}) {
  router.router?.on?.('routeDidChange', () => {
    const rawUrl = router.currentURL;
    if (!rawUrl || skipPrefixes.some(prefix => rawUrl.startsWith(prefix))) {
      return;
    }
    const url = stripToken ? stripAccessToken(rawUrl) : rawUrl;

    // Guard only the analytics calls: the UI cleanup below must run even when
    // gtag is unavailable (blocked or stripped inline snippet).
    if (typeof gtag === 'function') {
      gtag('event', 'page_view', {
        page_title: url,
        page_location: url,
        page_path: url,
        send_to: gaId
      });
    }
    if (pushDataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'page_view',
        page_title: url,
        page_location: stripAccessToken(window.location.href),
        page_path: url
      });
    }
    if (scrollSelector) {
      document.querySelectorAll(scrollSelector).forEach(el => {
        el.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    }
    if (dropdownCleanup) {
      document.querySelectorAll('.data-dropdown').forEach(element => {
        element.classList.remove('is-open');
      });
      document.body.classList.remove('dropdown-open');
    }
  });
}

export { setupRouterTracking, stripAccessToken };
//# sourceMappingURL=router-tracking.js.map
